import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { useAuth } from '@/hooks/common'
import { useSubscription } from '@/hooks/common/useSubscription'
import { FEATURE_KEYS, FeatureKey, FEATURES_MAP, isFeatureAvailableForTier } from '@/lib/featureKeys'
import { UpgradeModal } from '@/components/UpgradeModal'

// ─── Context shape ────────────────────────────────────────────────────────────

export interface UpgradeIntent {
  featureKey: FeatureKey
  /** Optional extra context for the modal headline. */
  headline?: string
  /** Override recommended plan (e.g. Studio when Gallery's room cap is hit). */
  upgradeTo?: 'Gallery' | 'Studio'
}

interface SubscriptionContextValue {
  tier: 'free' | 'pro' | 'enterprise'
  isPro: boolean
  isEnterprise: boolean
  loading: boolean
  /** Returns true if the user's tier grants access to the feature. */
  isFeatureAvailable: (key: FeatureKey) => boolean
  /**
   * Open the upgrade modal for a specific feature.
   * Call this when a gated action is attempted.
   */
  showUpgrade: (intent: UpgradeIntent) => void
  /** Dismiss the upgrade modal. */
  hideUpgrade: () => void
  /** The currently-pending upgrade intent (null when modal is closed). */
  upgradeIntent: UpgradeIntent | null
}

// ─── Context ──────────────────────────────────────────────────────────────────

const SubscriptionContext = createContext<SubscriptionContextValue>({
  tier: 'free',
  isPro: false,
  isEnterprise: false,
  loading: false,
  isFeatureAvailable: () => false,
  showUpgrade: () => undefined,
  hideUpgrade: () => undefined,
  upgradeIntent: null,
})

// ─── Provider ────────────────────────────────────────────────────────────────

export function SubscriptionProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()
  const { tier, isPro, isEnterprise, loading } = useSubscription(user?.userID)

  const [upgradeIntent, setUpgradeIntent] = useState<UpgradeIntent | null>(null)

  const isFeatureAvailable = useCallback(
    (key: FeatureKey) => isFeatureAvailableForTier(tier, key),
    [tier],
  )

  const showUpgrade = useCallback((intent: UpgradeIntent) => {
    setUpgradeIntent(intent)
  }, [])

  const hideUpgrade = useCallback(() => {
    setUpgradeIntent(null)
  }, [])

  const value = useMemo<SubscriptionContextValue>(
    () => ({ tier, isPro, isEnterprise, loading, isFeatureAvailable, showUpgrade, hideUpgrade, upgradeIntent }),
    [tier, isPro, isEnterprise, loading, isFeatureAvailable, showUpgrade, hideUpgrade, upgradeIntent],
  )

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
      <UpgradeModal
        open={upgradeIntent !== null}
        featureKey={upgradeIntent?.featureKey ?? null}
        headline={upgradeIntent?.headline}
        upgradeTo={upgradeIntent?.upgradeTo}
        onClose={hideUpgrade}
      />
    </SubscriptionContext.Provider>
  )
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Primary hook for feature gating. Use this anywhere you need to check a
 * plan gate or trigger the upgrade modal.
 *
 * @example
 *   const { isFeatureAvailable, showUpgrade } = useFeatureGate()
 *   if (!isFeatureAvailable(FEATURE_KEYS.GALLERY_3D)) {
 *     showUpgrade({ featureKey: FEATURE_KEYS.GALLERY_3D })
 *     return
 *   }
 *   navigate('/albums/' + id + '/to-3d')
 */
export function useFeatureGate(): SubscriptionContextValue {
  return useContext(SubscriptionContext)
}

export { FEATURE_KEYS, FEATURES_MAP }
